import React, { useState, useEffect } from 'react';
import { useTranslation } from './i18n/LanguageContext';

const LearningClock = ({ isDarkMode, userToken, API, showNotification, onBack }) => {
  const { t } = useTranslation();
  // App-shell caches: read synchronously so times + help + progress render
  // from frame 1. times is keyed per mode (learn/test1/test2) — switching
  // modes still flashes once until each mode is cached.
  const _readLcCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  // Default mode is 'learning' which loads 'learn' times — preload that one
  const _cachedLearnTimes = _readLcCache('lcTimesCache_learn');
  const _cachedHelp = _readLcCache('lcHelpCache');
  const _cachedProgress = _readLcCache('lcProgressCache');

  // Map URL slug ↔ internal mode value. URL uses friendly names; internal
  // state keeps the original 'testing1'/'testing2' values to avoid touching
  // the dozens of mode comparisons throughout the file.
  const _modeUrlToInternal = { 'read-and-choose': 'testing1', 'listen-and-pick-time': 'testing2' };
  const _modeInternalToUrl = { testing1: 'read-and-choose', testing2: 'listen-and-pick-time' };

  const _initialClockMode = (() => {
    try {
      const m = new URLSearchParams(window.location.search).get('mode');
      if (m && _modeUrlToInternal[m]) return _modeUrlToInternal[m];
    } catch {}
    return 'learning';
  })();
  const [mode, setMode] = useState(_initialClockMode); // 'learning', 'testing1', or 'testing2'

  // Mirror mode to URL on every change so refresh keeps the user on this tab.
  useEffect(() => {
    try {
      const params = new URLSearchParams();
      const slug = _modeInternalToUrl[mode];
      if (slug) params.set('mode', slug);
      const qs = params.toString();
      window.history.replaceState({}, '', '/games/LearningClock' + (qs ? '?' + qs : ''));
    } catch {}
  }, [mode]);

  const [times, setTimes] = useState(_cachedLearnTimes || []);
  const [currentTimeIndex, setCurrentTimeIndex] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [loading, setLoading] = useState(!_cachedLearnTimes);
  const [progress, setProgress] = useState(_cachedProgress);
  const [helpContent, setHelpContent] = useState(_cachedHelp);

  // Real-time Morocco time
  const [moroccoTime, setMoroccoTime] = useState(new Date());

  // Quiz state
  const [quizQuestion, setQuizQuestion] = useState(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizAttempts, setQuizAttempts] = useState(0);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(false);

  // Audio Quiz state (Mode 2)
  const [audioQuizQuestion, setAudioQuizQuestion] = useState(null);
  const [audioPlayed, setAudioPlayed] = useState(false);

  useEffect(() => {
    // Determine which mode to load times for
    let modeToLoad = 'learn';
    if (mode === 'testing1') modeToLoad = 'test1';
    else if (mode === 'testing2') modeToLoad = 'test2';

    // Show cached times for this mode immediately (silent refresh below)
    const cached = _readLcCache(`lcTimesCache_${modeToLoad}`);
    if (cached) {
      setTimes(cached);
      setLoading(false);
    }
    loadTimes(modeToLoad);
    loadHelpContent();

    if (userToken) {
      loadProgress();
    }

    // Update Morocco time every second
    const timeInterval = setInterval(() => {
      setMoroccoTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, [mode]);  // Reload times when mode changes

  const loadHelpContent = async () => {
    try {
      const response = await fetch(`${API}/learning-clock/help`);
      if (response.ok) {
        const data = await response.json();
        setHelpContent(data);
        try { localStorage.setItem('lcHelpCache', JSON.stringify(data)); } catch {}
      }
    } catch (error) {
      console.error('Error loading help content:', error);
    }
  };

  const loadTimes = async (modeToLoad = 'learn') => {
    try {
      const headers = userToken ? { 'Authorization': `Bearer ${userToken}` } : {};
      const response = await fetch(`${API}/learning-clock/times?mode=${modeToLoad}`, { headers });

      if (response.ok) {
        const data = await response.json();
        const list = data.times || [];
        setTimes(list);
        // Per-mode cache so switching modes uses cached times for that mode
        try { localStorage.setItem(`lcTimesCache_${modeToLoad}`, JSON.stringify(list)); } catch {}
      } else {
        console.error('Failed to load times:', response.status, await response.text());
        showNotification(t('clock_failed_load_times'), 'error');
        setTimes([]);
      }
    } catch (error) {
      console.error('Error loading times:', error);
      showNotification(t('clock_error_loading'), 'error');
      setTimes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await fetch(`${API}/learning-clock/progress`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProgress(data);
        try { localStorage.setItem('lcProgressCache', JSON.stringify(data)); } catch {}
        // NOTE: do NOT seed quizScore / quizAttempts from the server. Those track
        // the user's LIFETIME accuracy, but the on-screen "Score: X / Y" is a
        // SESSION counter that should start at 0 each time the user enters quiz
        // mode (and after clicking Reset). Backend still receives every answer
        // via /learning-clock/quiz-answer, so lifetime stats stay accurate.
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const handleNextTime = () => {
    setCurrentTimeIndex((prev) => (prev + 1) % times.length);
  };

  const handlePreviousTime = () => {
    setCurrentTimeIndex((prev) => (prev - 1 + times.length) % times.length);
  };

  // Sound-library URLs are stored as relative paths ("/api/sound-library/play/...")
  // and require a JWT. HTML5 <audio> can't send headers, so we (a) prefix the
  // backend origin (API ends in /api, strip it to get the origin) and (b) append
  // the JWT as ?token=. External URLs (legacy Dropbox) pass through unchanged.
  const resolveAudioUrl = (url) => {
    if (!url) return url;
    let resolved = url;
    if (url.startsWith('/api/')) {
      const origin = (API || '').replace(/\/api\/?$/, '');
      resolved = origin + url;
    }
    if (resolved.includes('/api/sound-library/play/') && !resolved.includes('token=')) {
      let tok = userToken;
      if (!tok) { try { tok = localStorage.getItem('user_token'); } catch {} }
      if (tok) resolved += (resolved.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(tok);
    }
    return resolved;
  };

  const playAudio = (audioUrl) => {
    if (!audioUrl || audioUrl.trim() === '') {
      showNotification(t('clock_audio_not_available'), 'warning');
      return;
    }

    try {
      const audio = new Audio(resolveAudioUrl(audioUrl));

      // Handle successful play
      audio.play()
        .then(() => {
          console.log('Audio playing:', audioUrl);
        })
        .catch((error) => {
          console.error('Audio playback error:', error);
          showNotification('Audio file not found. Please add audio URL in admin panel.', 'warning');
        });

      // Handle loading errors
      audio.onerror = () => {
        console.error('Audio load error:', audioUrl);
        showNotification('Could not load audio. Please check the audio URL in admin panel.', 'error');
      };

    } catch (error) {
      console.error('Error creating audio:', error);
      showNotification('Audio error. Please contact admin.', 'error');
    }
  };

  const startQuiz = (quizMode) => {
    setMode(quizMode);
    if (quizMode === 'testing1') {
      generateQuizQuestion();
    } else if (quizMode === 'testing2') {
      generateAudioQuizQuestion();
    }
  };

  const generateAudioQuizQuestion = () => {
    if (times.length === 0) return;

    // Safety: need at least 4 unique times for the quiz (1 correct + 3 wrong).
    // Without this guard the while-loop below runs forever and freezes the page.
    // We silently bail — admin-side config message shouldn't surface to end users.
    if (times.length < 4) return;

    // Pick random correct time
    const correctTime = times[Math.floor(Math.random() * times.length)];

    // Generate 3 wrong time options
    const wrongTimes = [];
    while (wrongTimes.length < 3) {
      const randomTime = times[Math.floor(Math.random() * times.length)];
      if (randomTime.id !== correctTime.id && !wrongTimes.find(t => t.id === randomTime.id)) {
        wrongTimes.push(randomTime);
      }
    }

    // Mix all 4 times
    const allTimes = [correctTime, ...wrongTimes].sort(() => Math.random() - 0.5);

    setAudioQuizQuestion({
      correctTime,
      timeOptions: allTimes
    });
    setShowQuizResult(false);
    setAudioPlayed(false);
  };

  const handleAudioQuizAnswer = async (selectedTime) => {
    const isCorrect = selectedTime.id === audioQuizQuestion.correctTime.id;
    setLastAnswerCorrect(isCorrect);
    setShowQuizResult(true);

    // Session-only counters — always increment locally regardless of login state.
    // Lifetime stats are tracked server-side via the /quiz-answer endpoint but are
    // intentionally NOT pulled back into the on-screen score (which is a per-session counter).
    setQuizAttempts(prev => prev + 1);
    if (isCorrect) setQuizScore(prev => prev + 1);

    // Record the answer on the backend (fire-and-forget) for lifetime accuracy stats.
    if (userToken) {
      try {
        const formData = new FormData();
        formData.append('time_id', audioQuizQuestion.correctTime.id);
        formData.append('is_correct', isCorrect);
        fetch(`${API}/learning-clock/quiz-answer`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${userToken}` },
          body: formData
        }).catch(() => {});
      } catch (error) {
        console.error('Error submitting answer:', error);
      }
    }

    // Auto-advance after 2 seconds
    setTimeout(() => {
      generateAudioQuizQuestion();
    }, 2000);
  };

  const generateQuizQuestion = () => {
    if (times.length === 0) return;

    // Safety: need at least 4 unique times for the quiz (1 correct + 3 wrong).
    // Without this guard the while-loop below runs forever and freezes the page.
    // We silently bail — admin-side config message shouldn't surface to end users.
    if (times.length < 4) return;

    // Pick random time
    const correctTime = times[Math.floor(Math.random() * times.length)];

    // Generate wrong answers
    const wrongAnswers = [];
    while (wrongAnswers.length < 3) {
      const randomTime = times[Math.floor(Math.random() * times.length)];
      if (randomTime.id !== correctTime.id && !wrongAnswers.find(t => t.id === randomTime.id)) {
        wrongAnswers.push(randomTime);
      }
    }

    // Mix correct and wrong answers
    const allAnswers = [correctTime, ...wrongAnswers].sort(() => Math.random() - 0.5);

    setQuizQuestion({
      correctTime,
      answers: allAnswers
    });
    setShowQuizResult(false);
  };

  const handleQuizAnswer = async (selectedTime) => {
    const isCorrect = selectedTime.id === quizQuestion.correctTime.id;
    setLastAnswerCorrect(isCorrect);
    setShowQuizResult(true);

    // Session-only counters — always increment locally regardless of login state.
    // Lifetime stats are tracked server-side via the /quiz-answer endpoint but are
    // intentionally NOT pulled back into the on-screen score (which is a per-session counter).
    setQuizAttempts(prev => prev + 1);
    if (isCorrect) setQuizScore(prev => prev + 1);

    // Record the answer on the backend (fire-and-forget) for lifetime accuracy stats.
    if (userToken) {
      try {
        const formData = new FormData();
        formData.append('time_id', quizQuestion.correctTime.id);
        formData.append('is_correct', isCorrect);
        fetch(`${API}/learning-clock/quiz-answer`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${userToken}` },
          body: formData
        }).catch(() => {});
      } catch (error) {
        console.error('Error submitting answer:', error);
      }
    }

    // Auto-advance to next question after 2 seconds
    setTimeout(() => {
      generateQuizQuestion();
    }, 2000);
  };

  // On mount/refresh in a quiz mode, auto-generate the first question once
  // times load. Without this, refreshing into ?mode=testing1 leaves the quiz blank.
  useEffect(() => {
    if (times.length === 0) return;
    if (mode === 'testing1' && !quizQuestion) {
      generateQuizQuestion();
    } else if (mode === 'testing2' && !audioQuizQuestion) {
      generateAudioQuizQuestion();
    }
    // eslint-disable-next-line
  }, [times, mode]);

  // Reset the current quiz mode — clears local score+attempts, hides any result card,
  // and generates a fresh new question (without an empty/loading intermediate).
  // Backend score is NOT reset (server keeps long-term accuracy stats). If the user is
  // logged in, local counters will be overwritten by the server on the next answer.
  const resetQuiz = () => {
    setQuizScore(0);
    setQuizAttempts(0);
    setShowQuizResult(false);
    setLastAnswerCorrect(false);
    setAudioPlayed(false);
    // Generate directly — these set the *Question state to a fresh value. We avoid
    // setting null first because that would briefly render the "Preparing..." spinner.
    if (mode === 'testing1') {
      generateQuizQuestion();
    } else if (mode === 'testing2') {
      generateAudioQuizQuestion();
    }
  };

  // Gradient classes per mode
  const gradientClass = mode === 'learning'
    ? isDarkMode ? 'from-teal-800 to-cyan-900' : 'from-teal-400 to-cyan-500'
    : mode === 'testing1'
      ? isDarkMode ? 'from-emerald-800 to-green-900' : 'from-emerald-400 to-green-500'
      : isDarkMode ? 'from-purple-800 to-violet-900' : 'from-purple-400 to-violet-500';

  if (loading) {
    return (
      <div className={`h-full overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`flex-1 flex items-center justify-center bg-gradient-to-br ${gradientClass}`}>
          <div className="text-center">
            <div className="animate-spin inline-block w-10 h-10 border-4 border-white/60 border-t-white rounded-full mb-3"></div>
            <p className="text-white/80 text-sm font-medium">{t('clock_loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!times || times.length === 0) {
    return (
      <div className={`h-full overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`flex-1 flex items-center justify-center bg-gradient-to-br ${gradientClass}`}>
          <div className="text-center px-6">
            <div className="text-5xl mb-3">🕐</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('clock_no_clocks')}</h2>
            <p className="text-white/70 text-sm mb-4">
              {t('clock_no_times_msg')}
            </p>
            <button
              onClick={onBack}
              className="px-5 py-2 rounded-full bg-white/20 text-white font-semibold text-sm hover:bg-white/30 transition-all"
            >
              {t('clock_back_to_games')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentTime = times[currentTimeIndex];

  return (
    <div className="h-full overflow-hidden flex flex-col">
      {/* Gradient Header Section */}
      <div className={`px-4 pt-2 pb-14 bg-gradient-to-br ${gradientClass}`}>
        {/* Back button */}
        <button
          onClick={onBack}
          className="text-white/80 text-sm font-medium mb-2 hover:text-white transition-colors"
        >
          {t('clock_back_to_games_arrow')}
        </button>

        {/* Title */}
        <div className="text-center mb-3">
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center justify-center gap-2">
            <span>🕐</span> {t('clock_title')}
          </h1>
          <p className="text-white/70 text-sm md:text-base">{t('clock_master_time')}</p>
        </div>

        {/* Mode Pills + Help */}
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setMode('learning')}
            className={`px-4 py-1.5 md:px-5 md:py-2 rounded-full font-semibold text-sm md:text-base transition-all ${
              mode === 'learning'
                ? 'bg-white/25 text-white shadow-sm'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            {t('clock_tab_learn')}
          </button>
          <button
            onClick={() => startQuiz('testing1')}
            className={`px-4 py-1.5 md:px-5 md:py-2 rounded-full font-semibold text-sm md:text-base transition-all ${
              mode === 'testing1'
                ? 'bg-white/25 text-white shadow-sm'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            {t('clock_tab_rc')}
          </button>
          <button
            onClick={() => startQuiz('testing2')}
            className={`px-4 py-1.5 md:px-5 md:py-2 rounded-full font-semibold text-sm md:text-base transition-all ${
              mode === 'testing2'
                ? 'bg-white/25 text-white shadow-sm'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            {t('clock_tab_lp')}
          </button>
          <button
            onClick={() => setShowHelp(true)}
            className="px-2.5 py-1.5 rounded-full text-white/60 hover:text-white/80 text-xs transition-all"
          >
            ❓
          </button>
        </div>

        {/* Score (testing modes) + Reset button */}
        {(mode === 'testing1' || mode === 'testing2') && (
          <div className="flex items-center justify-center gap-3 mt-2">
            <span className="text-white/80 text-xs md:text-sm font-medium">
              Score: {quizScore} / {quizAttempts}
            </span>
            <button
              onClick={resetQuiz}
              className="px-2.5 py-1 rounded-full text-[11px] md:text-xs font-bold bg-white/15 hover:bg-white/25 text-white transition-colors"
              title={t('clock_reset_tooltip')}
            >
              ↺ Reset
            </button>
          </div>
        )}
      </div>

      {/* White Content Card — overlaps gradient */}
      <div className={`flex-1 -mt-8 rounded-t-3xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} px-4 pt-5 pb-3 overflow-y-auto shadow-lg`}>
        {mode === 'learning' ? (
          <LearningMode
            currentTime={currentTime}
            handleNextTime={handleNextTime}
            handlePreviousTime={handlePreviousTime}
            playAudio={playAudio}
            isDarkMode={isDarkMode}
          />
        ) : mode === 'testing1' ? (
          <TestingMode
            quizQuestion={quizQuestion}
            showQuizResult={showQuizResult}
            lastAnswerCorrect={lastAnswerCorrect}
            handleQuizAnswer={handleQuizAnswer}
            playAudio={playAudio}
            isDarkMode={isDarkMode}
          />
        ) : mode === 'testing2' ? (
          <AudioTestingMode
            audioQuizQuestion={audioQuizQuestion}
            showQuizResult={showQuizResult}
            lastAnswerCorrect={lastAnswerCorrect}
            handleAudioQuizAnswer={handleAudioQuizAnswer}
            playAudio={playAudio}
            audioPlayed={audioPlayed}
            setAudioPlayed={setAudioPlayed}
            isDarkMode={isDarkMode}
          />
        ) : null}
      </div>

      {/* Help Modal */}
      {showHelp && (
        <HelpModal
          isDarkMode={isDarkMode}
          onClose={() => setShowHelp(false)}
          helpContent={helpContent}
        />
      )}
    </div>
  );
};

// Learning Mode Component
const LearningMode = ({
  currentTime,
  handleNextTime,
  handlePreviousTime,
  playAudio,
  isDarkMode
}) => {
  const { t } = useTranslation();
  if (!currentTime) {
    return (
      <div className="py-8 text-center">
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          No times available. Please add times from Admin Panel.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Section Title */}
      <h2 className={`text-base md:text-xl font-bold text-center mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        {t('clock_learn_in_darija')}
      </h2>

      {/* Clock Card */}
      <div className={`rounded-2xl p-4 mb-3 flex items-center justify-between ${
        isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
      }`}>
        <div className="flex-1 text-center">
          <div className={`text-3xl md:text-5xl font-bold font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {currentTime.hour}:{currentTime.minute.toString().padStart(2, '0')}
          </div>
          <div className={`text-sm md:text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {currentTime.am_pm}
          </div>
        </div>
        <button
          onClick={() => playAudio(currentTime.audio_url)}
          className={`w-10 h-10 flex items-center justify-center rounded-full transition-all ${
            isDarkMode ? 'bg-teal-600 hover:bg-teal-500' : 'bg-teal-500 hover:bg-teal-600'
          } text-white shadow-sm`}
          title={t('clock_listen_pronunciation')}
        >
          <span className="text-lg">🔊</span>
        </button>
      </div>

      {/* Labels */}
      <div className="mb-4 space-y-0">
        <div className={`flex items-center justify-between py-2.5 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <span className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>English</span>
          <span className={`text-sm md:text-base font-medium ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>{currentTime.english_label}</span>
        </div>
        <div className={`flex items-center justify-between py-2.5 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <span className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Darija</span>
          <span className={`text-sm md:text-base font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{currentTime.darija_label}</span>
        </div>
        {currentTime.darija_arabic && (
          <div className={`flex items-center justify-between py-2.5 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <span className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>العربية</span>
            <span className={`text-sm md:text-base font-medium ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} dir="rtl" style={{ fontFamily: 'Arial, sans-serif' }}>
              {currentTime.darija_arabic}
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-center gap-3">
        <button
          onClick={handlePreviousTime}
          className={`px-5 py-2 md:px-6 md:py-2.5 rounded-full font-semibold text-sm md:text-base transition-all ${
            isDarkMode
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          {t('clock_previous')}
        </button>
        <button
          onClick={handleNextTime}
          className={`px-5 py-2 md:px-6 md:py-2.5 rounded-full font-semibold text-sm md:text-base transition-all ${
            isDarkMode
              ? 'bg-teal-600 hover:bg-teal-500 text-white'
              : 'bg-teal-500 hover:bg-teal-600 text-white'
          } shadow-sm`}
        >
          {t('clock_next')}
        </button>
      </div>
    </div>
  );
};

// Testing Mode Component
const TestingMode = ({ quizQuestion, showQuizResult, lastAnswerCorrect, handleQuizAnswer, playAudio, isDarkMode }) => {
  const { t } = useTranslation();
  if (!quizQuestion) {
    return (
      <div className="py-8 text-center">
        <div className="animate-spin inline-block w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full mb-3"></div>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('clock_preparing_quiz')}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Section Title */}
      <div className="text-center mb-2">
        <h2 className={`text-base md:text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          {t('clock_read_and_choose')}
        </h2>
        <p className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {t('clock_pick_correct_label')}
        </p>
      </div>

      {/* Clock Card */}
      <div className={`rounded-2xl p-4 mb-3 flex items-center justify-between ${
        isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
      }`}>
        <div className="flex-1 text-center">
          <div className={`text-3xl md:text-5xl font-bold font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {quizQuestion.correctTime.hour}:{quizQuestion.correctTime.minute.toString().padStart(2, '0')}
          </div>
          <div className={`text-sm md:text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {quizQuestion.correctTime.am_pm}
          </div>
        </div>
        <button
          onClick={() => playAudio(quizQuestion.correctTime.audio_url)}
          className={`w-10 h-10 flex items-center justify-center rounded-full transition-all ${
            isDarkMode ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-emerald-500 hover:bg-emerald-600'
          } text-white shadow-sm`}
          title={t('clock_listen_pronunciation')}
        >
          <span className="text-lg">🔊</span>
        </button>
      </div>

      {/* Answer Options */}
      {!showQuizResult ? (
        <div className="grid grid-cols-2 gap-2">
          {quizQuestion.answers.map((answer) => (
            <button
              key={answer.id}
              onClick={() => handleQuizAnswer(answer)}
              className={`p-3 rounded-2xl text-left transition-all ${
                isDarkMode
                  ? 'bg-gray-700 hover:ring-2 hover:ring-emerald-400 text-white'
                  : 'bg-gray-50 hover:ring-2 hover:ring-emerald-400 text-gray-800'
              }`}
            >
              <div className={`text-sm md:text-base font-semibold mb-0.5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                {answer.darija_label}
              </div>
              {answer.darija_arabic && (
                <div className={`text-xs md:text-sm ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} dir="rtl" style={{ fontFamily: 'Arial, sans-serif' }}>
                  {answer.darija_arabic}
                </div>
              )}
            </button>
          ))}
        </div>
      ) : (
        <ResultCard
          lastAnswerCorrect={lastAnswerCorrect}
          isDarkMode={isDarkMode}
          correctLabel={quizQuestion.correctTime.darija_label}
          arabicLabel={quizQuestion.correctTime.darija_arabic}
        />
      )}
    </div>
  );
};

// Audio Testing Mode Component (Test Mode 2)
const AudioTestingMode = ({
  audioQuizQuestion,
  showQuizResult,
  lastAnswerCorrect,
  handleAudioQuizAnswer,
  playAudio,
  audioPlayed,
  setAudioPlayed,
  isDarkMode
}) => {
  const { t } = useTranslation();
  if (!audioQuizQuestion) {
    return (
      <div className="py-8 text-center">
        <div className="animate-spin inline-block w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full mb-3"></div>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('clock_preparing_quiz')}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Section Title */}
      <div className="text-center mb-3">
        <h2 className={`text-base md:text-xl font-bold mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          {t('clock_listen_pick_time')}
        </h2>

        {/* Audio Play Button */}
        <button
          onClick={() => {
            playAudio(audioQuizQuestion.correctTime.audio_url);
            setAudioPlayed(true);
          }}
          className={`px-5 py-2 md:px-6 md:py-2.5 rounded-full font-semibold text-sm md:text-base transition-all shadow-sm ${
            isDarkMode
              ? 'bg-purple-600 hover:bg-purple-500'
              : 'bg-purple-500 hover:bg-purple-600'
          } text-white`}
        >
          <span className="mr-1.5">🔊</span>
          {audioPlayed ? 'Play Again' : 'Play Audio'}
        </button>

        <p className={`text-xs md:text-sm mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {t('clock_listen_carefully')}
        </p>
      </div>

      {/* Time Options */}
      {!showQuizResult ? (
        <div className="grid grid-cols-2 gap-2">
          {audioQuizQuestion.timeOptions.map((timeOption) => (
            <button
              key={timeOption.id}
              onClick={() => handleAudioQuizAnswer(timeOption)}
              disabled={!audioPlayed}
              className={`p-3 rounded-2xl text-center transition-all ${
                !audioPlayed
                  ? isDarkMode
                    ? 'bg-gray-700 opacity-40 cursor-not-allowed'
                    : 'bg-gray-50 opacity-40 cursor-not-allowed'
                  : isDarkMode
                    ? 'bg-gray-700 hover:ring-2 hover:ring-purple-400'
                    : 'bg-gray-50 hover:ring-2 hover:ring-purple-400'
              }`}
            >
              <div className={`text-2xl md:text-3xl font-bold font-mono mb-0.5 ${
                !audioPlayed
                  ? isDarkMode ? 'text-gray-600' : 'text-gray-400'
                  : isDarkMode ? 'text-purple-400' : 'text-purple-600'
              }`}>
                {timeOption.hour}:{timeOption.minute.toString().padStart(2, '0')}
              </div>
              <div className={`text-xs font-medium mb-0.5 ${
                !audioPlayed
                  ? isDarkMode ? 'text-gray-600' : 'text-gray-400'
                  : isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {timeOption.am_pm}
              </div>
              <div className={`text-xs ${
                !audioPlayed
                  ? isDarkMode ? 'text-gray-700' : 'text-gray-400'
                  : isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {timeOption.darija_label}
              </div>
              {timeOption.darija_arabic && (
                <div className={`text-xs ${
                  !audioPlayed
                    ? isDarkMode ? 'text-gray-700' : 'text-gray-400'
                    : isDarkMode ? 'text-pink-300' : 'text-pink-600'
                }`} dir="rtl" style={{ fontFamily: 'Arial, sans-serif' }}>
                  {timeOption.darija_arabic}
                </div>
              )}
            </button>
          ))}
        </div>
      ) : (
        <ResultCard
          lastAnswerCorrect={lastAnswerCorrect}
          isDarkMode={isDarkMode}
          correctLabel={`${audioQuizQuestion.correctTime.hour}:${audioQuizQuestion.correctTime.minute.toString().padStart(2, '0')} ${audioQuizQuestion.correctTime.am_pm}`}
          darijaLabel={audioQuizQuestion.correctTime.darija_label}
          arabicLabel={audioQuizQuestion.correctTime.darija_arabic}
        />
      )}
    </div>
  );
};

// Shared Result Card
const ResultCard = ({ lastAnswerCorrect, isDarkMode, correctLabel, subtitle, darijaLabel, arabicLabel }) => {
  const { t } = useTranslation();
  return (
  <div className={`rounded-2xl p-4 text-center ${
    lastAnswerCorrect
      ? isDarkMode ? 'bg-green-900/30' : 'bg-green-50'
      : isDarkMode ? 'bg-red-900/30' : 'bg-red-50'
  }`}>
    <div className="text-2xl mb-1">{lastAnswerCorrect ? '✅' : '❌'}</div>
    <h3 className={`text-base font-semibold mb-1 ${
      lastAnswerCorrect
        ? isDarkMode ? 'text-green-300' : 'text-green-700'
        : isDarkMode ? 'text-red-300' : 'text-red-700'
    }`}>
      {lastAnswerCorrect ? t('clock_correct') : t('clock_try_again')}
    </h3>
    <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('clock_correct_answer')}</p>
    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
      {correctLabel}
    </div>
    {subtitle && (
      <div className={`text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
        {subtitle}
      </div>
    )}
    <div className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
      {darijaLabel}
    </div>
    {arabicLabel && (
      <div className={`text-sm font-medium ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} dir="rtl" style={{ fontFamily: 'Arial, sans-serif' }}>
        {arabicLabel}
      </div>
    )}
  </div>
  );
};

// Morocco Time Display Component
const MoroccoTimeDisplay = ({ moroccoTime, isDarkMode }) => {
  const getMoroccoTime = () => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Africa/Casablanca',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      return formatter.format(moroccoTime);
    } catch (error) {
      return moroccoTime.toLocaleTimeString('en-US', { hour12: true });
    }
  };

  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${
      isDarkMode
        ? 'bg-gradient-to-r from-red-900 to-green-900 border border-red-600'
        : 'bg-gradient-to-r from-red-50 to-green-50 border border-green-400'
    } shadow-sm`}>
      <span className="text-lg">🇲🇦</span>
      <div>
        <div className={`text-xs font-semibold uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Morocco
        </div>
        <div className={`text-sm font-bold font-mono ${
          isDarkMode ? 'text-green-400' : 'text-green-700'
        }`}>
          {getMoroccoTime()}
        </div>
      </div>
    </div>
  );
};

// Help Modal Component
const HelpModal = ({ isDarkMode, onClose, helpContent }) => {
  const { t } = useTranslation();
  const defaultContent = {
    title: t('clock_help_modal_title'),
    learn_mode: {
      title: "📚 Learning Mode:",
      items: [
        "Browse through different times using Next/Previous buttons",
        "Large digital time display for easy reading",
        "See time displayed in English and Darija",
        "Click audio button to hear pronunciation"
      ]
    },
    test1_mode: {
      title: "🎮 Test Mode 1 (Read & Choose):",
      items: [
        "See a random time on digital clock",
        "Choose the correct label from 4 options",
        "Get instant feedback (correct/incorrect)",
        "Track your score and progress"
      ]
    },
    test2_mode: {
      title: "🎧 Test Mode 2 (Listen & Pick):",
      items: [
        "Listen to audio pronunciation first",
        "See 4 different times displayed",
        "Click on the correct time you heard",
        "Perfect for audio recognition practice!"
      ]
    },
    features: {
      title: "⚙️ Features:",
      items: [
        "Large, clear digital time display",
        "Audio icon on clock for pronunciation",
        "Arabic script support (right-to-left)",
        "Two different test modes",
        "Progress tracking and score system",
        "Real-time Morocco time display"
      ]
    }
  };

  const content = helpContent || defaultContent;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
        <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {content.title}
        </h2>

        <div className="space-y-3 mb-4">
          {content.learn_mode && (
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className={`font-bold mb-1 text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                {content.learn_mode.title}
              </h3>
              <ul className={`list-disc list-inside space-y-0.5 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {content.learn_mode.items?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {content.test1_mode && (
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
              <h3 className={`font-bold mb-1 text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                {content.test1_mode.title}
              </h3>
              <ul className={`list-disc list-inside space-y-0.5 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {content.test1_mode.items?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {content.test2_mode && (
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
              <h3 className={`font-bold mb-1 text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                {content.test2_mode.title}
              </h3>
              <ul className={`list-disc list-inside space-y-0.5 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {content.test2_mode.items?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {content.features && (
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-pink-50'}`}>
              <h3 className={`font-bold mb-1 text-sm ${isDarkMode ? 'text-pink-300' : 'text-pink-700'}`}>
                {content.features.title}
              </h3>
              <ul className={`list-disc list-inside space-y-0.5 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {content.features.items?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-bold text-lg transition-all"
        >
          {t('clock_got_it')}
        </button>
      </div>
    </div>
  );
};

export default LearningClock;
